/**
 * SDK message → UI message transform for ivy-chat.
 * Converts raw Claude SDK messages (from NDJSON stream or history)
 * into structured UI messages for rendering.
 */

import { isProviderErrorMessage } from '@agentic-ivy/shared-types';
import { isImageReadNote } from './chatNotices';

export type MessageRole = 'user' | 'assistant';

export interface UIMessage {
  id: string;
  type: 'chat' | 'thinking' | 'tool_use' | 'tool_result' | 'system' | 'result';
  role?: MessageRole;
  content: string;
  timestamp: number;
  toolName?: string;
  toolInput?: Record<string, unknown>;
  toolUseId?: string;
  /** The Agent call this step belongs to, for a sub-agent's own tool calls. */
  parentToolUseId?: string;
  isError?: boolean;
  subtype?: string;
  /** On an error notice: the turn ended on it. A refused send or a lost connection says nothing about the turn. */
  turnFailed?: boolean;
  sessionId?: string;
  model?: string;
  costUsd?: number;
  durationMs?: number;
  inputTokens?: number;
  outputTokens?: number;
}

let messageCounter = 0;
function nextId(): string {
  return `msg-${++messageCounter}`;
}

/**
 * Detect compaction summary messages injected by Claude Code during
 * auto-compaction or manual /compact.
 */
function isCompactionSummary(text: string): boolean {
  if (!text.includes('continued from a previous conversation')) return false;
  return text.includes('summary below covers') || text.includes('Summary:');
}

function extractCompactionText(text: string): string {
  let cleaned = text
    .replace(/<div[^>]*>/gi, '')
    .replace(/<\/div>/gi, '')
    .replace(/<span[^>]*>/gi, '')
    .replace(/<\/span>/gi, '')
    .trim();
  const summaryIdx = cleaned.indexOf('Summary:');
  if (summaryIdx !== -1) {
    cleaned = cleaned.slice(summaryIdx + 'Summary:'.length).trim();
  }
  return cleaned;
}

/**
 * Content-based detection of sub-agent prompts.
 * Defense-in-depth — the backend filter should catch these first,
 * but this catches any that leak through (e.g., from DB history).
 */
function isSubAgentPrompt(text: string): boolean {
  if (text.length < 400) return false;
  if (/\/tmp\/\.ivy\/[a-f0-9-]{20,}\/slides\/slide-\d{3}/.test(text)) return true;
  if (/(?:Redesign|Build|Edit|Generate|Create|Update|Fix)\s+slide-\d{3}\s+for\s+project\s+[a-f0-9]{8}-/i.test(text)) return true;
  const agentHeaders = ['## What to change', '## Files to read', '## Files to write', '## Critical rules'];
  if (agentHeaders.filter(h => text.includes(h)).length >= 2) return true;
  return false;
}

const toolUseCache = new Map<string, { name: string; input: Record<string, unknown> }>();

/** A background task finishing: `<task-notification>` with its Agent call's id and a status. */
function taskNotification(text: string, timestamp: number): UIMessage | null {
  if (!text.trimStart().startsWith('<task-notification')) return null;
  const toolUseId = text.match(/<tool-use-id>([^<]+)<\/tool-use-id>/)?.[1]?.trim();
  const status = text.match(/<status>([^<]+)<\/status>/)?.[1]?.trim() || 'completed';
  return { id: nextId(), type: 'system', subtype: 'task_notification', content: status, timestamp, ...(toolUseId && { toolUseId }) };
}

export function processSDKMessage(sdkMessage: Record<string, unknown>): UIMessage[] {
  // The model provider's own error line, from an old server or a reconnect buffer: never a message.
  if (isProviderErrorMessage(sdkMessage)) return [];
  const messages: UIMessage[] = [];
  const timestamp = sdkMessage.timestamp
    ? new Date(sdkMessage.timestamp as string).getTime()
    : Date.now();

  switch (sdkMessage.type) {
    case 'system': {
      if (sdkMessage.subtype === 'init') {
        messages.push({
          id: nextId(),
          type: 'system',
          content: `Session initialized`,
          timestamp,
          subtype: 'init',
          sessionId: sdkMessage.session_id as string,
          model: sdkMessage.model as string,
        });
      } else if (sdkMessage.subtype === 'task_notification') {
        const toolUseId = typeof sdkMessage.tool_use_id === 'string' ? sdkMessage.tool_use_id : undefined;
        const status = typeof sdkMessage.status === 'string' ? sdkMessage.status : 'completed';
        messages.push({ id: nextId(), type: 'system', subtype: 'task_notification', content: status, timestamp, ...(toolUseId && { toolUseId }) });
      } else if (sdkMessage.subtype === 'compaction_summary') {
        const msg = sdkMessage.message as { content?: string } | undefined;
        const rawText = msg?.content || '';
        messages.push({
          id: nextId(),
          type: 'system',
          content: extractCompactionText(rawText),
          timestamp,
          subtype: 'compaction_summary',
        });
      }
      break;
    }

    case 'assistant': {
      const msg = sdkMessage.message as { content?: unknown[] } | undefined;
      if (msg?.content && Array.isArray(msg.content)) {
        for (const item of msg.content) {
          const block = item as Record<string, unknown>;

          if (block.type === 'thinking' && block.thinking) {
            messages.push({
              id: nextId(),
              type: 'thinking',
              role: 'assistant',
              content: block.thinking as string,
              timestamp,
            });
          } else if (block.type === 'text' && block.text) {
            messages.push({
              id: nextId(),
              type: 'chat',
              role: 'assistant',
              content: block.text as string,
              timestamp,
            });
          } else if (block.type === 'tool_use') {
            const toolId = block.id as string;
            const toolName = block.name as string;
            const toolInput = (block.input || {}) as Record<string, unknown>;
            toolUseCache.set(toolId, { name: toolName, input: toolInput });
            messages.push({
              id: nextId(),
              type: 'tool_use',
              role: 'assistant',
              content: toolName,
              timestamp,
              toolName,
              toolInput,
              toolUseId: toolId,
              ...(typeof sdkMessage.parent_tool_use_id === 'string' && { parentToolUseId: sdkMessage.parent_tool_use_id }),
            });
          }
        }
      }
      break;
    }

    case 'user': {
      const msg = sdkMessage.message as { content?: unknown } | undefined;
      const content = msg?.content;

      // Sub-agent prompts should not render as user bubbles.
      // The prompt is visible in the parent Agent tool card.
      if (sdkMessage.parent_tool_use_id) {
        if (typeof content === 'string') break;
        if (Array.isArray(content)) {
          for (const item of content) {
            const block = item as Record<string, unknown>;
            if (block.type === 'tool_result') {
              const toolUseId = block.tool_use_id as string;
              const cached = toolUseCache.get(toolUseId);
              const resultContent = typeof block.content === 'string'
                ? block.content
                : JSON.stringify(block.content);
              messages.push({
                id: nextId(),
                type: 'tool_result',
                content: resultContent,
                timestamp,
                toolName: cached?.name || 'Tool',
                toolUseId,
                parentToolUseId: sdkMessage.parent_tool_use_id as string,
                isError: block.is_error as boolean,
              });
            }
          }
          break;
        }
        break;
      }

      const notified = typeof content === 'string'
        ? taskNotification(content, timestamp)
        : Array.isArray(content)
          ? (content as Array<Record<string, unknown>>).map((b) => (b.type === 'text' && typeof b.text === 'string' ? taskNotification(b.text, timestamp) : null)).find(Boolean) ?? null
          : null;
      if (notified) {
        messages.push(notified);
        break;
      }

      // Defense-in-depth: catch sub-agent prompts that leaked past the backend filter, and a tool's image-read note
      if (typeof content === 'string' && (isSubAgentPrompt(content) || isImageReadNote(content))) {
        break;
      }

      // Defense-in-depth: catch compaction summaries that leaked past the backend filter
      if (typeof content === 'string' && isCompactionSummary(content)) {
        messages.push({
          id: nextId(),
          type: 'system',
          content: extractCompactionText(content),
          timestamp,
          subtype: 'compaction_summary',
        });
        break;
      }

      if (typeof content === 'string') {
        messages.push({
          id: nextId(),
          type: 'chat',
          role: 'user',
          content,
          timestamp,
        });
      } else if (Array.isArray(content)) {
        for (const item of content) {
          const block = item as Record<string, unknown>;
          if (block.type === 'text' && typeof block.text === 'string' && (isSubAgentPrompt(block.text) || isImageReadNote(block.text))) {
            // Skip sub-agent prompts and image-read notes in array content
          } else if (block.type === 'text' && typeof block.text === 'string' && isCompactionSummary(block.text)) {
            messages.push({
              id: nextId(),
              type: 'system',
              content: extractCompactionText(block.text),
              timestamp,
              subtype: 'compaction_summary',
            });
          } else if (block.type === 'text') {
            messages.push({
              id: nextId(),
              type: 'chat',
              role: 'user',
              content: block.text as string,
              timestamp,
            });
          } else if (block.type === 'tool_result') {
            const toolUseId = block.tool_use_id as string;
            const cached = toolUseCache.get(toolUseId);
            const resultContent = typeof block.content === 'string'
              ? block.content
              : JSON.stringify(block.content);
            messages.push({
              id: nextId(),
              type: 'tool_result',
              content: resultContent,
              timestamp,
              toolName: cached?.name || 'Tool',
              toolUseId,
              isError: block.is_error as boolean,
            });
          }
        }
      }
      break;
    }

    case 'result': {
      messages.push({
        id: nextId(),
        type: 'result',
        content: (sdkMessage.result as string) || 'Completed',
        timestamp,
        costUsd: sdkMessage.total_cost_usd as number,
        durationMs: sdkMessage.duration_ms as number,
        inputTokens: (sdkMessage.usage as Record<string, number>)?.input_tokens,
        outputTokens: (sdkMessage.usage as Record<string, number>)?.output_tokens,
      });
      break;
    }
  }

  return messages;
}

export function clearMessageParserCache(): void {
  toolUseCache.clear();
  messageCounter = 0;
}
